import React, { forwardRef, useMemo, useRef } from "react";
import { MultiSelect, MultiSelectProps } from "primereact/multiselect";
import { classNames } from "primereact/utils";
import { DashlyFieldShell } from "../shared/DashlyFieldShell";
import { useDashlyOverlayAppendTo } from "../shared/resolveDashlyOverlayAppendTo";
import { DashlyControlSize, dashlyControlSizeClass } from "../shared/dashlyControlSize";
import { DashlyControlVariant, dashlyControlVariantClass } from "../shared/dashlyControlVariant";
import { DashlyLeadingIcon, DashlyLeadingIconSlot, resolveLeadingIconClass } from "../shared/resolveLeadingIcon";
import "../shared/dashlyFormControl.scss";
import "./DashlyMultiSelect.scss";

export type DashlyMultiSelectProps = Omit<MultiSelectProps, "filter"> & {
    filter?: boolean;
    filterPlaceholder?: string;
    leadingIcon?: DashlyLeadingIcon;
    /** `filter` — list-page filter bar styling. */
    controlVariant?: DashlyControlVariant;
    /** Control height scale. Default `md` (2.5rem). */
    controlSize?: DashlyControlSize;
    /** Rendered inside the panel header (below filter). Use with panelHeaderTemplate for full control. */
    panelHeader?: React.ReactNode;
    label?: React.ReactNode;
    labelInfo?: string;
    hint?: React.ReactNode;
    required?: boolean;
    containerClassName?: string;
};

const isEnterKey = (event: { key?: string; code?: string }): boolean =>
    event.key === "Enter" || event.code === "Enter" || event.code === "NumpadEnter";

const isMultiSelectFilterInput = (target: EventTarget | null): boolean => {
    if (!(target instanceof HTMLElement)) {
        return false;
    }
    return target.classList.contains("p-multiselect-filter") || target.getAttribute("role") === "searchbox";
};

/** PrimeReact selects the focused option on Enter in the filter; with no matches that option is undefined/null. */
const suppressFilterEnterSelection = (event: React.KeyboardEvent): void => {
    if (!isEnterKey(event) || !isMultiSelectFilterInput(event.target)) {
        return;
    }
    event.preventDefault();
    event.stopPropagation();
    event.nativeEvent.stopImmediatePropagation();
};

const DashlyMultiSelect = forwardRef<MultiSelect, DashlyMultiSelectProps>(function DashlyMultiSelect(
    {
        leadingIcon,
        filter = false,
        filterPlaceholder,
        controlVariant = "form",
        controlSize = "md",
        panelHeader,
        label,
        labelInfo,
        hint,
        required,
        containerClassName,
        className,
        panelClassName,
        panelHeaderTemplate,
        itemTemplate,
        value,
        inputId,
        id,
        appendTo: appendToProp,
        filterInputAutoFocus,
        pt: ptProp,
        ...rest
    },
    ref
) {
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const overlayAppendTo = useDashlyOverlayAppendTo(wrapperRef);
    const resolvedAppendTo = appendToProp ?? overlayAppendTo;
    const resolvedInputId = inputId ?? id;
    const leadingIconClass = resolveLeadingIconClass(leadingIcon, value);

    const resolvedPanelHeaderTemplate = useMemo(() => {
        if (panelHeaderTemplate) {
            return panelHeaderTemplate;
        }
        if (!panelHeader) {
            return undefined;
        }
        return (event: { element: React.ReactNode }) => (
            <div className="dashly-multiselect-panel__header">
                {event.element}
                {panelHeader}
            </div>
        );
    }, [panelHeaderTemplate, panelHeader]);

    const resolvedPt = useMemo(() => {
        const existingPanel = ptProp && typeof ptProp === "object" ? ptProp.panel : undefined;
        const existingPanelProps =
            existingPanel && typeof existingPanel === "object" && !Array.isArray(existingPanel)
                ? (existingPanel as React.HTMLAttributes<HTMLDivElement>)
                : {};
        return {
            ...ptProp,
            panel: {
                ...existingPanelProps,
                onKeyDownCapture: (event: React.KeyboardEvent<HTMLDivElement>) => {
                    suppressFilterEnterSelection(event);
                    existingPanelProps.onKeyDownCapture?.(event);
                },
            },
        };
    }, [ptProp]);

    const resolvedItemTemplate = useMemo(() => {
        if (itemTemplate) {
            return itemTemplate;
        }
        if (!leadingIcon) {
            return undefined;
        }
        return (option: unknown) => {
            const iconClass = resolveLeadingIconClass(leadingIcon, option);
            const labelText = rest.optionLabel
                ? (option as Record<string, unknown>)?.[rest.optionLabel as string]
                : String(option ?? "");
            return (
                <span className="dashly-multiselect__item">
                    <DashlyLeadingIconSlot iconClass={iconClass} />
                    <span>{labelText != null ? String(labelText) : ""}</span>
                </span>
            );
        };
    }, [itemTemplate, leadingIcon, rest.optionLabel]);

    const control = (
        <div
            ref={wrapperRef}
            className={classNames(
                "dashly-multiselect-wrap",
                dashlyControlSizeClass(controlSize),
                leadingIconClass && "dashly-multiselect-wrap--with-icon",
            )}
        >
            {leadingIconClass ? (
                <DashlyLeadingIconSlot
                    iconClass={leadingIconClass}
                    className="dashly-multiselect-wrap__leading-icon"
                />
            ) : null}
            <MultiSelect
                ref={ref}
                {...rest}
                id={resolvedInputId}
                inputId={resolvedInputId}
                value={value}
                filter={filter}
                filterPlaceholder={filterPlaceholder}
                filterInputAutoFocus={filterInputAutoFocus ?? filter}
                itemTemplate={resolvedItemTemplate}
                panelHeaderTemplate={resolvedPanelHeaderTemplate}
                className={classNames(
                    dashlyControlVariantClass("dashly-multiselect", controlVariant),
                    "w-full",
                    className,
                )}
                panelClassName={classNames(
                    controlVariant === "filter"
                        ? "dashly-filter-multiselect-panel"
                        : "dashly-multiselect-panel",
                    panelClassName,
                )}
                appendTo={resolvedAppendTo}
                pt={resolvedPt}
            />
        </div>
    );

    if (!label) {
        return control;
    }

    return (
        <DashlyFieldShell
            label={label}
            labelInfo={labelInfo}
            hint={hint}
            htmlFor={resolvedInputId}
            required={required}
            className={containerClassName}
        >
            {control}
        </DashlyFieldShell>
    );
});

DashlyMultiSelect.displayName = "DashlyMultiSelect";

export { DashlyMultiSelect };
